import React, { useEffect, useMemo, useState } from 'react'

import { useAuth as useClerkAuth } from '@clerk/clerk-react'

import { navigate, routes, useLocation, useParams } from '@cedarjs/router'

import FeatureFlagDevPanel from 'src/components/FeatureFlagDevPanel/FeatureFlagDevPanel'
import { LoadingSpin } from 'src/components/shared/StyledComponents'
import { isProductionMirror } from 'src/constants'
import { useAppStateQuery } from 'src/fetch/appState'
import { useBroadcastChannelListener } from 'src/hooks/use-broadcast-channel-listener'
import { useMyOrganizationMemberships } from 'src/hooks/use-my-organization-memberships'
import { SessionStorageKey } from 'src/types'
import { getImageRequestViewportWidth } from 'src/utils/images'
import { useMonitoring } from 'src/utils/monitoring'
import { getActiveOrgIdFromStorage } from 'src/utils/org-storage'

import {
  useOrganizationByRecordTemplateUuidQuery,
  useOrganizationByRecordTypeIdQuery,
  useOrganizationByRecordUuidQuery,
  useOrganizationBySlugQuery,
} from '../../fetch/organizations'
import { clearRedirect } from '../../hooks/use-auth-redirect'
import { useIsInIFrame } from '../../hooks/use-is-in-iframe'
import { useSetUpIntercom } from '../../hooks/use-set-up-intercom'
import StatsigProvider from '../../utils/featureFlagging/StatsigProvider'

import ProdCopyAccessDenied from './ProdCopyAccessDenied'
import { AppStateContext, AppStateContextData } from './types'
import { useReleaseNotification } from './use-release-notification'

/**
 * Root layout that resolves the active organization and provides it to the
 * entire app tree via AppStateContext.
 *
 * ## Multi-org / multi-tab model
 *
 * A user can belong to multiple organizations. Each browser tab independently
 * tracks which org is active, so a user can work in Org A in one tab and
 * Org B in another without interference.
 *
 * Storage strategy:
 * - **sessionStorage** (`ActiveOrgId`) — per-tab. Holds the active org for
 *   this specific tab. Survives in-tab navigations but is scoped to the tab's
 *   lifetime. Written when the user explicitly switches orgs or when
 *   navigating to an explicit-org page (e.g. /record/{uuid}).
 * - **localStorage** (`LastSelectedOrgId`) — cross-tab. Stores the last org
 *   the user explicitly chose. New tabs that don't yet have a sessionStorage
 *   value fall back to this, so the most-recently-used org carries over.
 *
 * On mount, activeOrgId is resolved via `getActiveOrgIdFromStorage`
 * (sessionStorage -> localStorage -> first membership fallback). When the
 * user navigates to an explicit-org page belonging to a different org, the
 * effect on `organizationByPath` updates activeOrgId and sessionStorage so
 * the sidebar, navigation, and AppState query all reflect the correct org
 * without a full page reload.
 *
 * All downstream consumers read the active org via `useMyOrgId()` /
 * `useMyOrg()` from the context this layout provides.
 */
type Props = {
  children?: React.ReactNode
}
const AppStateContextLayout = ({ children }: Props) => {
  const { isLoaded, isSignedIn, actor } = useClerkAuth()
  const memberships = useMyOrganizationMemberships()

  // Resolve the active org from storage before context exists.
  // This is the single source of truth for the active org ID — all consumers
  // read it via useMyOrgId() from the context this layout provides.
  //
  // activeOrgId is state (not a memo) so it can be updated reactively when
  // organizationByPath resolves to a different org (explicit-org page navigation).
  const membershipOrgIds = useMemo(
    () => new Set(memberships.map((m) => m.organizationId)),
    [memberships]
  )

  const [activeOrgId, setActiveOrgId] = useState<number | undefined>(() => {
    return getActiveOrgIdFromStorage(membershipOrgIds) ?? memberships[0]?.organizationId
  })

  // Sync when memberships load after mount (Clerk auth may not be ready on first render)
  useEffect(() => {
    if (!activeOrgId && memberships.length > 0) {
      setActiveOrgId(getActiveOrgIdFromStorage(membershipOrgIds) ?? memberships[0]?.organizationId)
    }
  }, [activeOrgId, memberships, membershipOrgIds])

  const {
    data: { organization, user },
    isLoading: isLoadingAppState,
    refetch,
  } = useAppStateQuery(activeOrgId)

  useEffect(() => {
    clearRedirect()
  }, [])

  useSetUpIntercom({ me: user, organization })
  useBroadcastChannelListener()
  useReleaseNotification()

  const { pathname } = useLocation()
  const { slug, recordTypeId, recordUuid, recordTemplateUuid } = useParams()
  const viewportWidth = getImageRequestViewportWidth()

  // Admin org pages: you're managing a customer org, not switching into it.
  // Pathname check is interim — M3 org-switch routing moves this to a shared route exclusion list.
  const isAdminOrgDetailPage =
    pathname.startsWith(`${routes.adminOrgs()}/`) && pathname !== routes.newOrg()

  const { organizationBySlug, isLoading: isLoadingOrganizationBySlug } = useOrganizationBySlugQuery(
    {
      slug: slug ? decodeURIComponent(slug) : undefined,
      viewportWidth,
    }
  )
  const {
    organization: organizationByRecordTemplateUuid,
    isLoading: isLoadingOrganizationByRecordTemplateUuid,
  } = useOrganizationByRecordTemplateUuidQuery(recordTemplateUuid, { viewportWidth })

  const { organization: organizationByRecordTypeId } = useOrganizationByRecordTypeIdQuery(
    recordTypeId ? Number(recordTypeId) : undefined,
    viewportWidth,
    {
      enabled: !!recordTypeId && !!user,
    }
  )
  const { organization: organizationByRecordUuid } = useOrganizationByRecordUuidQuery(
    recordUuid,
    viewportWidth,
    {
      enabled: !!recordUuid && !!user,
    }
  )
  const organizationByResource =
    organizationByRecordTemplateUuid ??
    organizationByRecordTypeId ??
    organizationByRecordUuid ??
    undefined

  const organizationByPath = organizationBySlug ?? organizationByResource ?? undefined

  const isLoadingOrganizationByPath =
    isLoadingOrganizationBySlug ||
    (!!recordTemplateUuid && isLoadingOrganizationByRecordTemplateUuid)

  useEffect(() => {
    if ((slug || recordTemplateUuid) && !organizationByPath && !isLoadingOrganizationByPath) {
      navigate(routes.notFound())
    }
  }, [isLoadingOrganizationByPath, organizationByPath, slug, recordTemplateUuid])

  // When navigating to an explicit-org page (e.g. /record/{uuid}) that belongs to a
  // different org, update activeOrgId so the AppState query re-fires with the correct
  // org and the sidebar/navigation update without requiring a full page refresh.
  // GovWell staff can access resource pages cross-org without membership; the bypass
  // applies only when the org is resolved from a resource param, not from URL slug alone
  // (e.g. /admin/orgs/{slug} manages another org without switching active context).
  useEffect(() => {
    if (isAdminOrgDetailPage) {
      return
    }

    const canSwitchWithoutMembership = user?.isGovWellStaff && !!organizationByResource

    if (
      organizationByPath?.id &&
      organizationByPath.id !== activeOrgId &&
      (membershipOrgIds.has(organizationByPath.id) || canSwitchWithoutMembership)
    ) {
      setActiveOrgId(organizationByPath.id)
      sessionStorage.setItem(SessionStorageKey.ActiveOrgId, String(organizationByPath.id))
    }
  }, [
    organizationByPath?.id,
    organizationByResource,
    activeOrgId,
    isAdminOrgDetailPage,
    membershipOrgIds,
    user?.isGovWellStaff,
  ])

  const [currentlyViewedRecordTypeId, setCurrentlyViewedRecordTypeId] = useState<
    number | undefined
  >(undefined)
  const contextData: AppStateContextData = {
    user,
    isLoading: isLoadingAppState || isLoadingOrganizationByPath,
    organization,
    organizationByPath,
    recordTypes:
      user?.recordTypeAccesses
        ?.map((rta) => rta.recordType)
        .filter((rt) => !activeOrgId || rt.organizationId === activeOrgId) || [],
    currentlyViewedRecordTypeId,
    setCurrentlyViewedRecordTypeId,
    refetch,
  }

  useMonitoring(contextData)
  const isInIFrame = useIsInIFrame()

  // In prod-copy the backend rejects non-staff users in getCurrentUser, so
  // the AppState query errors and user stays undefined. Catch that before
  // the loading guard so blocked users see the access denied screen instead
  // of an infinite spinner.
  if (isProductionMirror() && isSignedIn && !user && !isLoadingAppState && !actor) {
    return <ProdCopyAccessDenied />
  }

  if (
    !isLoaded || // Not ready to make decision about which layout to show
    (!isSignedIn && isLoadingOrganizationByPath) || // Not ready to render topbar for unauthenticated users; DO NOT show org loading state for authenticated users, will cause layout flash on record application page
    (isSignedIn && !user) // Not ready to render sidebar for authenticated users
  ) {
    return !isInIFrame ? <LoadingSpin /> : null
  }

  return (
    <AppStateContext.Provider value={contextData}>
      <StatsigProvider>
        {children}
        <FeatureFlagDevPanel />
      </StatsigProvider>
    </AppStateContext.Provider>
  )
}

export default AppStateContextLayout
